import React, { useState, useEffect, useCallback, useRef } from 'react';
import { emailsApi, EmailSettings, EmailLog, EmailStats, EmailJobSchedule, SchedulerJobStatus } from '../../api/emailsApi';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Icons } from '../../components/common/Icons';
import './EmailNotifications.css';

// Email type definitions for display
const EMAIL_TYPES = [
  {
    key: 'email.missing_timesheet_enabled',
    type: 'missing_timesheet',
    label: 'Missing Timesheet',
    description: 'Notify employees who haven\'t filled their timesheet for working days in the current month.',
    schedule: 'Daily at 10:00 AM IST',
    triggerable: true,
  },
  {
    key: 'email.missing_login_enabled',
    type: 'missing_login',
    label: 'Missing Login',
    description: 'Alert employees who haven\'t logged in (no swipe record) on a working day.',
    schedule: 'Daily at 9:50 AM IST',
    triggerable: true,
  },
  {
    key: 'email.milestone_alert_enabled',
    type: 'milestone_alert',
    label: 'Milestone Alert',
    description: 'Notify project managers when a project milestone is approaching (2 days before) or due today.',
    schedule: 'Daily at 9:00 AM IST',
    triggerable: true,
  },
  {
    key: 'email.billable_hours_alert_enabled',
    type: 'billable_hours_complete',
    label: 'Billable Hours Complete',
    description: 'Alert PMOs when a project\'s logged billable hours reach or exceed the budgeted hours.',
    schedule: 'Daily at 11:00 AM IST',
    triggerable: true,
  },
  {
    key: 'email.weekly_report_enabled',
    type: 'weekly_missing_report',
    label: 'Weekly Missing Report',
    description: 'Send managers a weekly summary of employees with missing timesheets from the previous week.',
    schedule: 'Every Monday at 10:00 AM IST',
    triggerable: true,
  },
  {
    key: 'email.project_assignment_enabled',
    type: 'project_assignment',
    label: 'Project Assignment',
    description: 'Notify employees when they are assigned to a new project.',
    schedule: 'Event-driven (on assignment)',
    triggerable: false,
  },
];

export function EmailNotifications() {
  const [settings, setSettings] = useState<EmailSettings>({});
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [stats, setStats] = useState<EmailStats>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Logs pagination & filters
  const [logPage, setLogPage] = useState(1);
  const [logPages, setLogPages] = useState(1);
  const [logTotal, setLogTotal] = useState(0);
  const [logTypeFilter, setLogTypeFilter] = useState('');
  const [logStatusFilter, setLogStatusFilter] = useState('');
  const [logSearch, setLogSearch] = useState('');
  const [logSearchInput, setLogSearchInput] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Test SMTP state
  const [testEmail, setTestEmail] = useState('');
  const [testingSMTP, setTestingSMTP] = useState(false);

  // Email job schedules
  const [schedules, setSchedules] = useState<Record<string, EmailJobSchedule>>({});
  // Draft times while editing (job_id -> "HH:MM")
  const [scheduleDrafts, setScheduleDrafts] = useState<Record<string, string>>({});
  const [savingSchedule, setSavingSchedule] = useState<string | null>(null);
  // Live APScheduler status (next fire times)
  const [schedulerStatus, setSchedulerStatus] = useState<Record<string, SchedulerJobStatus>>({});

  // Test-mode allowlist state (superadmin-only redirect of all outgoing mails)
  const [testRecipientInput, setTestRecipientInput] = useState('');
  const testModeEnabled = settings['email.test_mode_enabled'] === true;
  const testRecipients: string[] = Array.isArray(settings['email.test_recipients'])
    ? (settings['email.test_recipients'] as string[])
    : [];

  const persistTestRecipients = async (next: string[]) => {
    const prev = settings;
    setSettings(s => ({ ...s, 'email.test_recipients': next }));
    try {
      setSaving(true);
      await emailsApi.updateSettings({ 'email.test_recipients': next as any });
    } catch (err) {
      setSettings(prev);
      setError(err instanceof Error ? err.message : 'Failed to save test recipients');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTestRecipient = async () => {
    const value = testRecipientInput.trim();
    if (!value) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError('Invalid email address');
      return;
    }
    if (testRecipients.includes(value)) {
      setTestRecipientInput('');
      return;
    }
    const next = [...testRecipients, value];
    setTestRecipientInput('');
    await persistTestRecipients(next);
  };

  const handleRemoveTestRecipient = async (email: string) => {
    const next = testRecipients.filter(e => e !== email);
    await persistTestRecipients(next);
  };

  const handleToggleTestMode = async () => {
    const newValue = !testModeEnabled;
    if (newValue && testRecipients.length === 0) {
      setError('Add at least one test recipient before enabling Test Mode.');
      return;
    }
    const prev = settings;
    setSettings(s => ({ ...s, 'email.test_mode_enabled': newValue }));
    try {
      setSaving(true);
      await emailsApi.updateSettings({ 'email.test_mode_enabled': newValue });
      setSuccess(newValue ? 'Test Mode ENABLED — all emails will redirect to the allowlist.' : 'Test Mode disabled.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setSettings(prev);
      setError(err instanceof Error ? err.message : 'Failed to toggle test mode');
    } finally {
      setSaving(false);
    }
  };
  const fetchSettings = useCallback(async () => {
    try {
      const response = await emailsApi.getSettings();
      setSettings(response.settings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load email settings');
    }
  }, []);

  const fetchSchedules = useCallback(async () => {
    try {
      const response = await emailsApi.getSchedules();
      setSchedules(response.schedules);
      // Initialise drafts from current values
      const drafts: Record<string, string> = {};
      Object.entries(response.schedules).forEach(([id, s]) => {
        drafts[id] = s.time_ist;
      });
      setScheduleDrafts(drafts);
    } catch (err) {
      console.error('Failed to load email schedules:', err);
    }
  }, []);

  const fetchSchedulerStatus = useCallback(async () => {
    try {
      const response = await emailsApi.getSchedulerStatus();
      setSchedulerStatus(response.jobs || {});
    } catch (err) {
      console.error('Failed to load scheduler status:', err);
    }
  }, []);

  const handleScheduleChange = (jobId: string, value: string) => {
    setScheduleDrafts(d => ({ ...d, [jobId]: value }));
  };

  const handleScheduleSave = async (jobId: string) => {
    const time = scheduleDrafts[jobId];
    if (!time) return;
    // Validate HH:MM
    if (!/^\d{1,2}:\d{2}$/.test(time)) {
      setError('Time must be in HH:MM format');
      return;
    }
    const [h, m] = time.split(':').map(Number);
    if (h > 23 || m > 59) {
      setError('Invalid time value');
      return;
    }
    const normalized = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    const prevSchedules = { ...schedules };
    setSchedules(s => ({
      ...s,
      [jobId]: { ...s[jobId], time_ist: normalized },
    }));
    try {
      setSavingSchedule(jobId);
      await emailsApi.updateSchedules({ [jobId]: normalized });
      setSuccess(`Schedule updated — "${schedules[jobId]?.label}" will now run at ${normalized} IST`);
      setTimeout(() => setSuccess(null), 4000);
      // Refresh live status to confirm reschedule applied
      await fetchSchedulerStatus();
    } catch (err) {
      setSchedules(prevSchedules);
      setError(err instanceof Error ? err.message : 'Failed to save schedule');
    } finally {
      setSavingSchedule(null);
    }
  };

  const handleScheduleReset = async (jobId: string) => {
    const defaultTime = schedules[jobId]?.default_time;
    if (!defaultTime) return;
    setScheduleDrafts(d => ({ ...d, [jobId]: defaultTime }));
    const prevSchedules = { ...schedules };
    setSchedules(s => ({
      ...s,
      [jobId]: { ...s[jobId], time_ist: defaultTime },
    }));
    try {
      setSavingSchedule(jobId);
      await emailsApi.updateSchedules({ [jobId]: defaultTime });
      setSuccess(`"${schedules[jobId]?.label}" reset to default ${defaultTime} IST`);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setSchedules(prevSchedules);
      setError(err instanceof Error ? err.message : 'Failed to reset schedule');
    } finally {
      setSavingSchedule(null);
    }
  };

  const fetchLogs = useCallback(async (page = 1) => {
    try {
      const response = await emailsApi.getLogs({
        page,
        per_page: 15,
        type: logTypeFilter || undefined,
        status: logStatusFilter || undefined,
        search: logSearch || undefined,
      });
      setLogs(response.logs);
      setLogPage(response.page);
      setLogPages(response.pages);
      setLogTotal(response.total);
    } catch (err) {
      console.error('Failed to load email logs:', err);
    }
  }, [logTypeFilter, logStatusFilter, logSearch]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await emailsApi.getStats();
      setStats(response.stats);
    } catch (err) {
      console.error('Failed to load email stats:', err);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchSettings(), fetchLogs(1), fetchStats(), fetchSchedules(), fetchSchedulerStatus()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchSettings, fetchLogs, fetchStats, fetchSchedules, fetchSchedulerStatus]);

  useEffect(() => {
    fetchLogs(1);
  }, [logTypeFilter, logStatusFilter, logSearch, fetchLogs]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLogSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setLogSearch(value.trim());
    }, 350);
  };

  const handleSearchClear = () => {
    setLogSearchInput('');
    setLogSearch('');
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  };

  const handleToggle = async (key: string, currentValue: boolean) => {
    const newValue = !currentValue;
    const prev = { ...settings };
    setSettings(s => ({ ...s, [key]: newValue }));

    try {
      setSaving(true);
      await emailsApi.updateSettings({ [key]: newValue });
      setSuccess(`${newValue ? 'Enabled' : 'Disabled'} successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setSettings(prev);
      setError(err instanceof Error ? err.message : 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const handleTrigger = async (emailType: string) => {
    try {
      setTriggering(emailType);
      setError(null);
      const response = await emailsApi.triggerEmail(emailType);
      setSuccess(response.message || `${emailType} job triggered successfully`);
      setTimeout(() => setSuccess(null), 5000);
      // Refresh logs and stats after trigger
      await Promise.all([fetchLogs(logPage), fetchStats()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to trigger ${emailType}`);
    } finally {
      setTriggering(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr + (dateStr.endsWith('Z') ? '' : 'Z'));
    return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  const formatEmailType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getTotalSent = () => {
    return Object.values(stats).reduce((sum, s) => sum + (s.sent || 0), 0);
  };

  const getTotalFailed = () => {
    return Object.values(stats).reduce((sum, s) => sum + (s.failed || 0), 0);
  };

  const handleTestSMTP = async () => {
    if (!testEmail.trim()) {
      setError('Please enter an email address');
      return;
    }
    try {
      setTestingSMTP(true);
      setError(null);
      const response = await emailsApi.testSmtp(testEmail.trim());
      if (response.success) {
        setSuccess(response.message);
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'SMTP test failed');
    } finally {
      setTestingSMTP(false);
    }
  };

  if (loading) {
    return (
      <div className="email-page">
        <div className="email-loading">
          <div className="loader-spinner" />
          <p>Loading email settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="email-page">
      <header className="email-header">
        <div className="email-title">
          <h1>Email Notifications</h1>
          <p>Configure automated email notifications and view delivery logs</p>
        </div>
      </header>

      {error && (
        <div className="alert alert-error">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <Icons.Check />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>&times;</button>
        </div>
      )}

      {/* Stats Overview */}
      <div className="email-stats-row">
        <div className="stat-card">
          <div className="stat-value">{Object.keys(stats).length}</div>
          <div className="stat-label">Active Types</div>
        </div>
        <div className="stat-card">
          <div className="stat-value stat-sent">{getTotalSent()}</div>
          <div className="stat-label">Emails Sent</div>
        </div>
        <div className="stat-card">
          <div className="stat-value stat-failed">{getTotalFailed()}</div>
          <div className="stat-label">Failed</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{logTotal}</div>
          <div className="stat-label">Total Logs</div>
        </div>
      </div>

      {/* Test Mode (allowlist redirect) */}
      <Card className="email-card">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 320px', minWidth: 280 }}>
            <h2 className="card-title" style={{ marginBottom: 4 }}>
              Test Mode {testModeEnabled && <span style={{ background: '#fff3cd', color: '#856404', padding: '2px 10px', borderRadius: 12, fontSize: 12, marginLeft: 8 }}>ON</span>}
            </h2>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>
              When ON, every email triggered by the scheduler or "Send Now" buttons is
              redirected to the allowlist below instead of the real recipient. Use this
              to verify cron and content changes without spamming staff.
            </p>
          </div>
          <label className="toggle-switch" title={testRecipients.length === 0 && !testModeEnabled ? 'Add at least one recipient first' : ''}>
            <input
              type="checkbox"
              checked={testModeEnabled}
              onChange={handleToggleTestMode}
              disabled={saving}
            />
            <span className="slider"></span>
          </label>
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="email"
            className="test-smtp-input"
            placeholder="add-test-recipient@example.com"
            value={testRecipientInput}
            onChange={(e) => setTestRecipientInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTestRecipient(); } }}
            disabled={saving}
            style={{ flex: 1, minWidth: 220 }}
          />
          <Button
            variant="primary"
            onClick={handleAddTestRecipient}
            disabled={saving || !testRecipientInput.trim()}
          >
            Add Recipient
          </Button>
        </div>

        {testRecipients.length === 0 ? (
          <p style={{ marginTop: 12, color: '#9ca3af', fontSize: 13, fontStyle: 'italic' }}>
            No test recipients yet. Test Mode cannot be enabled until you add at least one.
          </p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            {testRecipients.map(email => (
              <span
                key={email}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: '#eef2ff', color: '#3730a3',
                  padding: '4px 10px', borderRadius: 14, fontSize: 13,
                  border: '1px solid #c7d2fe',
                }}
              >
                {email}
                <button
                  type="button"
                  onClick={() => handleRemoveTestRecipient(email)}
                  disabled={saving}
                  aria-label={`Remove ${email}`}
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: '#3730a3', fontSize: 16, lineHeight: 1, padding: 0,
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {/* ---- Job Schedule Overrides ---- */}
        <div style={{ marginTop: 24, borderTop: '1px solid var(--border-primary)', paddingTop: 20 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
            Job Schedule Overrides <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)' }}>(IST)</span>
          </h3>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-secondary)' }}>
            Change when each email job fires. Saved immediately and applied without restarting the server.
          </p>
          {Object.keys(schedules).length === 0 ? (
            <p style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>
              Loading schedules… (restart the backend server if this persists)
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {Object.entries(schedules).map(([jobId, sched]) => {
                const draft = scheduleDrafts[jobId] ?? sched.time_ist;
                const isChanged = draft !== sched.time_ist;
                const isDefaultVal = sched.time_ist === sched.default_time;
                const isSaving = savingSchedule === jobId;
                const status = schedulerStatus[jobId];
                const toggleOff = status && !status.toggle_on;
                return (
                  <div
                    key={jobId}
                    style={{
                      background: 'var(--bg-secondary, #f9fafb)',
                      border: `1px solid ${toggleOff ? '#fca5a5' : 'var(--border-primary)'}`,
                      borderRadius: 10,
                      padding: '12px 14px',
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>
                      {sched.label}
                    </div>
                    {sched.day_of_week && (
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>
                        Day: {sched.day_of_week.charAt(0).toUpperCase() + sched.day_of_week.slice(1)}
                      </div>
                    )}
                    {/* Next fire time badge */}
                    {status?.next_run_ist ? (
                      <div style={{ fontSize: 11, color: '#059669', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span>⏰</span>
                        <span>Next: <b>{status.next_run_ist}</b></span>
                      </div>
                    ) : status ? (
                      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>No next run scheduled</div>
                    ) : null}
                    {/* Toggle off warning */}
                    {toggleOff && (
                      <div style={{ fontSize: 11, color: '#dc2626', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span>⚠️</span>
                        <span>Toggle is OFF — job runs but skips in production (not in test mode)</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                      <input
                        type="time"
                        value={draft}
                        onChange={(e) => handleScheduleChange(jobId, e.target.value)}
                        disabled={isSaving}
                        style={{
                          flex: 1,
                          padding: '5px 8px',
                          borderRadius: 6,
                          border: `1px solid ${isChanged ? '#6366f1' : 'var(--border-primary)'}`,
                          fontSize: 13,
                          background: 'var(--card-bg, #fff)',
                          color: 'var(--text-primary)',
                          outline: 'none',
                        }}
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleScheduleSave(jobId)}
                        disabled={isSaving || !isChanged}
                      >
                        {isSaving ? '…' : 'Save'}
                      </Button>
                      {!isDefaultVal && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleScheduleReset(jobId)}
                          disabled={isSaving}
                          title={`Reset to default (${sched.default_time} IST)`}
                        >
                          ↺
                        </Button>
                      )}
                    </div>
                    {!isDefaultVal && (
                      <div style={{ fontSize: 11, color: '#6366f1', marginTop: 4 }}>
                        Default: {sched.default_time} IST
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {/* Test SMTP Connection */}
      <Card className="email-card">
        <h2 className="card-title">Test SMTP Connection</h2>
        <p className="test-smtp-description">
          Send a test email to verify your SMTP configuration is working correctly.
        </p>
        <div className="test-smtp-row">
          <input
            type="email"
            className="test-smtp-input"
            placeholder="Enter recipient email address..."
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleTestSMTP(); }}
            disabled={testingSMTP}
          />
          <Button
            variant="primary"
            onClick={handleTestSMTP}
            disabled={testingSMTP || !testEmail.trim()}
          >
            {testingSMTP ? 'Sending...' : 'Send Test Email'}
          </Button>
        </div>
      </Card>

      {/* Toggle Settings */}
      <Card className="email-card">
        <h2 className="card-title">Notification Toggles</h2>
        <div className="email-toggles-list">
          {EMAIL_TYPES.map((emailType) => {
            const enabled = !!settings[emailType.key];
            return (
              <div key={emailType.key} className="email-toggle-item">
                <div className="email-toggle-info">
                  <div className="email-toggle-header">
                    <span className="email-toggle-label">{emailType.label}</span>
                    <span className="email-schedule-badge">{emailType.schedule}</span>
                  </div>
                  <p className="email-toggle-description">{emailType.description}</p>
                  {stats[emailType.type] && (
                    <div className="email-type-stats">
                      <span className="mini-stat sent">
                        {stats[emailType.type].sent || 0} sent
                      </span>
                      {(stats[emailType.type].failed || 0) > 0 && (
                        <span className="mini-stat failed">
                          {stats[emailType.type].failed} failed
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="email-toggle-actions">
                  {emailType.triggerable && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTrigger(emailType.type)}
                      disabled={triggering === emailType.type || saving}
                    >
                      {triggering === emailType.type ? 'Running...' : 'Send Now'}
                    </Button>
                  )}
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={() => handleToggle(emailType.key, enabled)}
                      disabled={saving}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Email Logs */}
      <Card className="email-card">
        <div className="logs-header">
          <h2 className="card-title" style={{ margin: 0, paddingBottom: 0, borderBottom: 'none' }}>
            Delivery Logs
          </h2>
          <div className="logs-filters">
            <div className="log-search-wrapper">
              <Icons.Search />
              <input
                type="text"
                className="log-search-input"
                placeholder="Search by name or email..."
                value={logSearchInput}
                onChange={handleSearchChange}
              />
              {logSearchInput && (
                <button className="log-search-clear" onClick={handleSearchClear} aria-label="Clear search">
                  &times;
                </button>
              )}
            </div>
            <select
              value={logTypeFilter}
              onChange={(e) => setLogTypeFilter(e.target.value)}
              className="log-filter-select"
            >
              <option value="">All Types</option>
              {EMAIL_TYPES.map(et => (
                <option key={et.type} value={et.type}>{et.label}</option>
              ))}
            </select>
            <select
              value={logStatusFilter}
              onChange={(e) => setLogStatusFilter(e.target.value)}
              className="log-filter-select"
            >
              <option value="">All Statuses</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        {logs.length === 0 ? (
          <div className="empty-state">
            <Icons.Email />
            <h3>No Email Logs</h3>
            <p>Email delivery logs will appear here once notifications are sent.</p>
          </div>
        ) : (
          <>
            <div className="email-table-wrapper">
              <table className="email-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Recipient</th>
                    <th>Status</th>
                    <th>Reference Date</th>
                    <th>Sent At</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td>
                        <span className="email-type-badge">
                          {formatEmailType(log.email_type)}
                        </span>
                      </td>
                      <td>
                        <div className="recipient-cell">
                          <span className="recipient-name">{log.recipient_name || '—'}</span>
                          {log.recipient_email && (
                            <span className="recipient-email">{log.recipient_email}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge status-${log.status}`}>
                          {log.status}
                        </span>
                      </td>
                      <td>{log.reference_date || '—'}</td>
                      <td>{formatDate(log.sent_at)}</td>
                      <td>
                        {log.error_message ? (
                          <span className="error-text" title={log.error_message}>
                            {log.error_message.length > 40
                              ? log.error_message.substring(0, 40) + '...'
                              : log.error_message}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {logPages > 1 && (
              <div className="logs-pagination">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchLogs(logPage - 1)}
                  disabled={logPage <= 1}
                >
                  Previous
                </Button>
                <span className="page-info">
                  Page {logPage} of {logPages} ({logTotal} total)
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchLogs(logPage + 1)}
                  disabled={logPage >= logPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default EmailNotifications;
